import { DateOnly } from '@if/core/date'
import { EProductsInsurance, EProvince } from '@if/core/types'
import { normalizeNumber } from '@if/core/utils'
import { ControlledTextField } from '@if/mui'
import { Box, CardContent, CardHeader, Divider, MenuItem, Stack, Typography } from '@mui/material'
import { CardBorderTop } from '@src/components/common/Card'
import { ProductsProvider } from '@src/types/ProductsProvider'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useEffect, useMemo } from 'react'
import { Control, FieldErrors, UseFormGetValues, UseFormSetValue, UseFormTrigger, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ProductsInsuranceDetails from './ProductsInsuranceDetails'

interface Props {
  insurancesProvider: ProductsProvider[]
  warrantyProvider: ProductsProvider[]
  control: Control<ProductsWorksheet>
  setValue: UseFormSetValue<ProductsWorksheet>
  errors: FieldErrors<ProductsWorksheet>
  getValues: UseFormGetValues<ProductsWorksheet>
  trigger: UseFormTrigger<ProductsWorksheet>
  stateIso: EProvince
  hasContractEndDate: boolean
}

const ProductsInsurances = ({
  insurancesProvider,
  warrantyProvider,
  control,
  setValue,
  stateIso,
  hasContractEndDate,
}: Props) => {
  const { t } = useTranslation()
  const today = useMemo(() => {
    const now = new Date()
    return DateOnly.fromComponents(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  }, [])

  const replacementOrGapInsurance = useWatch({
    control,
    name: 'replacementOrGapInsurance',
  })

  const creditInsurance = useWatch({
    control,
    name: 'creditInsurance',
  })

  const extendedWarranty = useWatch({
    control,
    name: 'extendedWarranty',
  })

  useEffect(() => {
    const getPremium = (value: number | null | undefined) => {
      const amount = Number(value)
      return Number.isNaN(amount) ? 0 : amount
    }

    const amount = normalizeNumber(
      getPremium(replacementOrGapInsurance?.amount) +
        getPremium(creditInsurance?.amount) +
        getPremium(extendedWarranty?.amount),
    )

    setValue('amountRequested', amount)
  }, [setValue, replacementOrGapInsurance?.amount, creditInsurance?.amount, extendedWarranty?.amount])

  const insuranceProviders = useMemo<JSX.Element[]>(() => {
    return insurancesProvider.map((provider) => (
      <MenuItem key={provider.id} value={provider.id}>
        {provider.name}
      </MenuItem>
    ))
  }, [insurancesProvider])

  const warrantyProviders = useMemo<JSX.Element[]>(() => {
    return warrantyProvider.map((provider) => (
      <MenuItem key={provider.id} value={provider.id}>
        {provider.name}
      </MenuItem>
    ))
  }, [warrantyProvider])

  return (
    <Stack direction={{ lg: 'row', md: 'column' }} spacing={1} justifyContent="space-between">
      <CardBorderTop>
        <CardHeader title={t('productsWorksheet.insurances')} />
        <CardContent>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            {/* Replacement / GAP Insurance */}
            <Box>
              <ControlledTextField
                select
                control={control}
                name="replacementOrGapInsurance.provider"
                label={t('common.provider')}
                fullWidth
              >
                <MenuItem value="" />
                {insuranceProviders}
              </ControlledTextField>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 2 }}>
                {stateIso === EProvince.Quebec
                  ? t('productsWorksheet.replacementInsurance')
                  : t('productsWorksheet.gapInsurance')}
              </Typography>
              <ProductsInsuranceDetails
                control={control}
                insurance={EProductsInsurance.ReplacementOrGapInsurance}
                today={today}
                setValue={setValue}
                hasContractEndDate={hasContractEndDate}
              />
            </Box>

            {/* Credit Insurance */}
            <Box>
              <ControlledTextField
                select
                control={control}
                name="creditInsurance.provider"
                label={t('common.provider')}
                fullWidth
              >
                <MenuItem value="" />
                {insuranceProviders}
              </ControlledTextField>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 2 }}>
                {t('productsWorksheet.creditInsurance')}
              </Typography>
              <ProductsInsuranceDetails
                control={control}
                insurance={EProductsInsurance.CreditInsurance}
                today={today}
                setValue={setValue}
                hasContractEndDate={hasContractEndDate}
              />
            </Box>
          </Stack>
        </CardContent>
      </CardBorderTop>
      <CardBorderTop>
        <CardHeader title={t('productsWorksheet.extendedWarranty')} />
        <CardContent>
          <ControlledTextField
            select
            control={control}
            name="extendedWarranty.provider"
            label={t('common.provider')}
            fullWidth
          >
            <MenuItem value="" />
            {warrantyProviders}
          </ControlledTextField>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 2 }}>
              {t('productsWorksheet.extendedWarranty')}
            </Typography>
            <ProductsInsuranceDetails
              control={control}
              insurance={EProductsInsurance.ExtendedWarranty}
              today={today}
              setValue={setValue}
              hasContractEndDate={hasContractEndDate}
            />
          </Box>
        </CardContent>
      </CardBorderTop>
    </Stack>
  )
}

export default ProductsInsurances
